<template>
  <div>
    <dl class="grid grid-cols-[auto_1fr] gap-x-6 gap-y-2 text-sm">
      <dt class="text-slate-500">Status</dt>
      <dd class="flex items-center gap-2">
        {{ statusPresentation.label }}
        <SvgIcon
          v-if="statusPresentation.icon"
          :class="statusPresentation.icon.className"
          :path="statusPresentation.icon.path"
          size="18"
        />
      </dd>

      <dt class="text-slate-500">Totalt belopp att betala</dt>
      <dd>{{ formatAmount(detail.amounts.invoiced.value) }} kr</dd>

      <dt class="text-slate-500">Inbetalt</dt>
      <dd>
        {{ formatAmount(detail.amounts.paid.value) }} kr
        <span class="text-slate-500">({{ formatAmount(detail.amounts.remaining.value) }} kr kvar)</span>
      </dd>

      <dt class="text-slate-500">Betalningsmetod</dt>
      <dd>{{ translatePaymentMethod(detail.paymentMethod) }}</dd>

      <template v-if="detail.fileId || detail.reminders.length">
        <dt class="text-slate-500">PDF</dt>
        <dd class="flex flex-wrap items-center gap-3">
          <AppButton
            v-if="detail.fileId"
            small
            color="transparent"
            :loading="openingId === detail.fileId"
            :aria-label="`Visa faktura ${detail.invoiceNumber}`"
            @click="openInvoiceFile(detail.fileId, `Faktura ${detail.invoiceNumber}`)"
          >
            <template #prepend>
              <SvgIcon :path="mdiFilePdfBox" size="18" />
            </template>
            Faktura
          </AppButton>

          <AppButton
            v-for="reminder in detail.reminders"
            :key="reminder.fileId"
            small
            color="transparent"
            :loading="openingId === reminder.fileId"
            :aria-label="`Visa påminnelse ${formatDateIfValid(reminder.createdDate)}`"
            @click="openInvoiceFile(reminder.fileId, `Påminnelse ${formatDateIfValid(reminder.createdDate)}`)"
          >
            <template #prepend>
              <SvgIcon :path="mdiFilePdfBox" size="18" />
            </template>
            Påminnelse {{ formatDateIfValid(reminder.createdDate) }}
          </AppButton>
        </dd>
      </template>
    </dl>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { mdiFilePdfBox } from '@mdi/js'
import { AppButton, SvgIcon } from '@staff/ui'
import { formatDateIfValid, formatAmount } from '@staff/shared'
import { useMetadata } from '@/composables/useMetadata'
import { getInvoicePaymentStatus, invoiceStatusPresentation } from './invoiceStatus'
import { useFilePreview } from '@/composables/previewModal'
import { fetchInvoiceFile, type InvoiceDetail } from '@/services/invoice'

const props = defineProps<{
  detail: InvoiceDetail
}>()

const { getReferenceValues } = useMetadata()
const { openingId, openFile } = useFilePreview()

const statusPresentation = computed(() => invoiceStatusPresentation[getInvoicePaymentStatus(props.detail)])

const translatePaymentMethod = (method: string) => {
  const values = getReferenceValues('paymentMethods')
  const match = values.find((v) => v.code === method)
  return match?.label ?? method
}

const openInvoiceFile = (fileId: string, title: string) => {
  void openFile({
    id: fileId,
    title,
    load: () => fetchInvoiceFile(props.detail.id, fileId),
    fallbackContentType: 'application/pdf',
    fallbackFileName: `invoice-${fileId}.pdf`,
  })
}
</script>

<style scoped lang="scss">
.status-paid {
  color: var(--color-icon-success);
}

.status-overdue {
  color: var(--color-icon-warning);
}
</style>
